// src/components/DocumentList.js z dodaną funkcjonalnością eksportu zbiorczego
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Container,
    Typography,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Tooltip
} from '@mui/material';
import {
    Receipt as ReceiptIcon,
    Description as DescriptionIcon,
    CloudUpload as UploadIcon,
    Refresh as RefreshIcon,
    FileDownload as DownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BulkExportDialog from './BulkExportDialog';

const API_URL = process.env.REACT_APP_API_URL || '';

const DocumentList = () => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const navigate = useNavigate();
    const token = localStorage.getItem('token');

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/expenses`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDocuments(response.data);
            setError(null);
        } catch (error) {
            console.error('Error fetching documents:', error);
            setError('Nie udało się pobrać listy dokumentów');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, [token]);

    // Parse document data and extract important fields
    const parseDocumentData = (doc) => {
        let parsedData = {};

        // Try to parse AI extracted data if available
        if (doc.ai_extracted_data) {
            try {
                parsedData = typeof doc.ai_extracted_data === 'string'
                    ? JSON.parse(doc.ai_extracted_data)
                    : doc.ai_extracted_data;
            } catch (e) {
                console.error('Error parsing AI data for document', doc.id, e);
            }
        }

        // Extract document fields with fallbacks
        return {
            id: doc.id,
            documentNumber: parsedData.documentNumber || doc.invoice_number || '-',
            issueDate: (parsedData.dates && parsedData.dates.issueDate) || doc.issue_date || '-',
            paymentMethod: (parsedData.payment && parsedData.payment.method) || doc.payment_method || '-',
            amount: (parsedData.amounts && parsedData.amounts.gross)
                ? `${parsedData.amounts.gross.toFixed(2)} ${parsedData.amounts.currency || 'PLN'}`
                : doc.gross_amount
                    ? `${doc.gross_amount} PLN`
                    : '-',
            status: doc.processing_status || 'completed',
            documentType: parsedData.documentType || (doc.invoice_number ? 'invoice' : 'receipt'),
            pageCount: doc.page_count || 1
        };
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" gutterBottom>
                        Lista dokumentów
                    </Typography>
                    <Box>
                        <Tooltip title="Eksportuj wszystkie dokumenty do CSV">
                            <Button
                                variant="outlined"
                                onClick={() => setExportDialogOpen(true)}
                                startIcon={<DownloadIcon />}
                                sx={{ mr: 1 }}
                            >
                                Eksport CSV
                            </Button>
                        </Tooltip>

                        <Button
                            variant="outlined"
                            onClick={fetchDocuments}
                            startIcon={<RefreshIcon />}
                            sx={{ mr: 1 }}
                        >
                            Odśwież
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate('/expenses/upload')}
                            startIcon={<UploadIcon />}
                        >
                            Dodaj dokument
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Paper sx={{ width: '100%', overflowX: 'auto' }}>
                    {loading ? (
                        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Typ</TableCell>
                                    <TableCell>Numer dokumentu</TableCell>
                                    <TableCell>Data wystawienia</TableCell>
                                    <TableCell>Kwota</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Akcje</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {documents.map(doc => {
                                    const parsedDoc = parseDocumentData(doc);

                                    return (
                                        <TableRow key={parsedDoc.id}>
                                            <TableCell>{parsedDoc.id}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    icon={parsedDoc.documentType === 'invoice' ? <DescriptionIcon /> : <ReceiptIcon />}
                                                    label={parsedDoc.documentType === 'invoice' ? 'Faktura' : 'Paragon'}
                                                    color={parsedDoc.documentType === 'invoice' ? 'primary' : 'secondary'}
                                                />
                                                {parsedDoc.pageCount > 1 && (
                                                    <Tooltip title={`Dokument ${parsedDoc.pageCount}-stronicowy`}>
                                                        <Chip
                                                            size="small"
                                                            label={`${parsedDoc.pageCount} str.`}
                                                            color="info"
                                                            sx={{ ml: 0.5 }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell>{parsedDoc.documentNumber}</TableCell>
                                            <TableCell>{parsedDoc.issueDate}</TableCell>
                                            <TableCell>{parsedDoc.amount}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={
                                                        parsedDoc.status === 'processing' ? 'Przetwarzanie' :
                                                            parsedDoc.status === 'completed' ? 'Zakończono' :
                                                                parsedDoc.status
                                                    }
                                                    color={
                                                        parsedDoc.status === 'processing' ? 'warning' :
                                                            parsedDoc.status === 'completed' ? 'success' :
                                                                'default'
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => navigate(`/expenses/${parsedDoc.id}`)}
                                                >
                                                    Szczegóły
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {documents.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            Brak dokumentów. Kliknij "Dodaj dokument" aby rozpocząć.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Paper>
            </Box>

            {/* Dialog eksportu */}
            <BulkExportDialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
            />
        </Container>
    );
};

export default DocumentList;